import React, { PropTypes, PureComponent } from 'react';
import _ from 'underscore';
import QueryPagination from '../../../common/components/QueryPagination';
import EmptyState from '../../../common/components/EmptyState';
import { Icon, InverseButton, LinkButton, LoadingWrapper } from 'react-fast-ui';
import PageTitle from '../../../PageTitle';
import AdminGameTable from '../AdminGameTable';
import { NOTIFICATION_CONTEXT_TYPES } from '../../../NotificationSystemProvider';
import AdminGamesFilter from '../forms/AdminGamesFilter';
import TourneyFilterDataProvider from '../../models/TourneyFilterDataProvider';
import AdminGameModal from '../../../components/modals/AdminGameModal';
import EnterScoreModal from '../../../components/modals/EnterScoreModal';
import moment from 'moment-timezone';
import SwapTeamsModal from '../../../components/modals/SwapTeamsModal';
import EditAllModal from '../../../components/modals/EditAllGamesModal';
import queuePromises from '../../../util/queuePromises';
import { updateQuery } from '../../../util/routerUtil';
import SuperAdminFeature from '../../../common/access/SuperAdminFeature';
import AdminFeature from '../../../common/access/AdminFeature';

const EMPTY_OBJECT = {};
const DEFAULT_SORT = 'A|scheduled';
const NO_GAMES = <EmptyState>No matching games!</EmptyState>;

const fromQueryParams = (query = {}, filters = {}) => {
  const {
    gameNameSearch,
    incomplete,
    divisionId,
    gameGroupId,
    groupType,
    venueId,
    courtId,
    team,
    missingTeam,
    localDate,
    tourneyTeamId,
    sort,
  } = query;
  const { divisions, gameGroups, groupTypes, venues, courts, teams, dates } = filters;
  let state = { sort };

  if (gameNameSearch) {
    state.gameNameSearch = gameNameSearch;
  }
  if (localDate) {
    state.date = _.find(dates, { id: localDate });
  }
  if (team) {
    state.team = _.find(dates, { id: query.tourneyTeamId.id });
  }
  if (gameGroupId) {
    state.gameGroup = _.find(gameGroups, { id: gameGroupId });
  } else if (divisionId) {
    state.division = _.find(divisions, { id: divisionId });
  }
  if (courtId) {
    state.court = _.find(courts, { id: courtId });
  } else if (venueId) {
    state.venue = _.find(venues, { id: venueId });
  }
  if (incomplete) {
    if (incomplete === 'true') {
      state.incomplete = true;
    } else if (incomplete === 'false') {
      state.incomplete = false;
    }
  }

  if (missingTeam) {
    if (missingTeam === 'true') {
      state.missingTeam = true;
    } else if (missingTeam === 'false') {
      state.missingTeam = false;
    }
  }

  if (groupType) {
    state.groupType = _.find(groupTypes, { id: groupType });
  }

  if (tourneyTeamId) {
    state.team = _.find(teams, { id: tourneyTeamId });
  }

  return state;
};

export const toQueryParams = filter => {
  let empty;
  if (!filter) {
    return null;
  }
  const {
    court,
    date,
    division,
    gameGroup,
    gameNameSearch,
    groupType,
    incomplete,
    missingTeam,
    sort,
    team,
    venue,
    start,
    count,
  } = filter;

  const query = { gameNameSearch, start: start && `${start}`, count: count && `${count}`, sort };

  query.localDate = date ? date.id : null;
  query.tourneyTeamId = team ? team.id : null;
  query.gameGroupId = gameGroup ? gameGroup.id : null;
  query.divisionId = division ? division.id : null;
  query.courtId = court ? court.id : null;

  if (incomplete === 'true' || incomplete === 'false') {
    query.incomplete = incomplete;
  } else if (incomplete === true || incomplete === false) {
    query.incomplete = incomplete.toString();
  } else {
    query.incomplete = empty;
  }

  if (missingTeam === 'true' || missingTeam === 'false') {
    query.missingTeam = missingTeam;
  } else if (missingTeam === true || missingTeam === false) {
    query.missingTeam = missingTeam.toString();
  } else {
    query.missingTeam = empty;
  }

  query.groupType = groupType ? groupType.id : null;

  if (!court) {
    query.venueId = venue ? venue.id : null;
  }

  return query;
};

export default class AdminGamesListPage extends PureComponent {
  static contextTypes = {
    dao: PropTypes.object.isRequired,
    router: PropTypes.object.isRequired,
    ...NOTIFICATION_CONTEXT_TYPES
  };

  static propTypes = {
    params: PropTypes.shape({
      tourneyId: PropTypes.string.isRequired,
      data: PropTypes.string
    }).isRequired,
    tourney: PropTypes.object
  };

  static defaultProps = {};

  state = {
    filterData: null,
    games: null,
    totalCount: 0,
    promise: null,
    editing: null,
    scoring: null,
    editAll: null,
    swapping: false
  };

  componentDidMount() {
    this._filterDataProvider = new TourneyFilterDataProvider(this.context.dao);
    this.fetchData();
  }

  componentWillReceiveProps({ location: { query: nextQuery } }) {
    if (!_.isEqual(nextQuery, this.props.location.query)) {
      this.debouncedFetchData();
    }
  }

  shouldComponentUpdate({ location: { query: { nextQuery } } }, { filterData, games }) {
    return this.props.location.query !== nextQuery ||
      filterData !== this.state.filterData ||
      games !== this.state.games;
  }

  changeParams = (change) => {
    const { router } = this.context;
    const { location } = this.props;
    const { filterData } = this.state;
    updateQuery(router, location, toQueryParams({ ...fromQueryParams(location.query, filterData), ...change }));
  };

  changeFilter = filter => this.changeParams({ ...filter, start: 0 });
  handleSort = sort => this.changeParams({ sort });

  fetchData = () => {
    const { onError, dao } = this.context;
    const { location: { query }, tourney } = this.props;
    const { promise } = this.state;

    if (!!promise) {
      promise.cancel();
    }

    const { sort } = fromQueryParams(query);

    const queryParams = {
      tourneyId: tourney.id,
      sort: DEFAULT_SORT,
      ...QueryPagination.getPageParams(query),
      ...query,
    };

    this.setState({
      promise: this._filterDataProvider.getFilterData(tourney)
        .then(
          filterData => {
            if (this.state.filterData !== filterData) {
              this.setState({ filterData, games: [] });
            }
            return dao.games.get(queryParams);
          }
        )
        .then(({ totalCount, results: games }) => this.setState({ totalCount, games }))
        .catch(onError)
        .finally(() => this.setState({ promise: null }))
    });
  };

  debouncedFetchData = _.debounce(this.fetchData, 300);

  handleEditGame = editing => this.setState({ editing });
  cancelEdit = () => this.handleEditGame(null);

  handleSaveEdit = () => {
    const { onSuccess, onError, dao } = this.context;
    const { editing, promise } = this.state;

    if (!!promise) {
      return;
    }

    this.setState({
      editing: null,
      promise: dao.games.save(editing)
        .then(game => onSuccess(`Saved game: "${game.name}"`))
        .catch(err => {
          onError(err);
          this.setState({ editing });
        })
        .finally(this.fetchData)
    });
  };

  handleEditScoring = scoring => this.setState({ scoring });
  cancelEditScoring = () => this.handleEditScoring(null);
  handleEnterScore = () => {
    const { dao, onError, onSuccess } = this.context;
    const { scoring, promise } = this.state;

    if (!!promise) {
      promise.cancel();
    }

    this.setState({
      scoring: null,
      promise: dao.updateGameScore({
        gameId: scoring.id,
        homeScore: scoring.sides.home.score,
        visitorScore: scoring.sides.visitor.score
      })
        .then(() => onSuccess(`Score entered for game: ${scoring.id}`))
        .catch(onError)
        .finally(this.fetchData)
    });
  };

  addGame = () => {
    const { tourney } = this.props;
    const {
      params: { filter: { gameNameSearch, division, gameGroup, venue, court, date } = {} } = {},
      filterData: { gameGroups, courts }
    } = this.state;

    const defaultGameGroup = gameGroup || (
        division ?
          _.first(_.filter(gameGroups), ({ division: { id } }) => id === division.id) || _.first(gameGroups) :
          _.first(gameGroups)
      );

    const defaultCourt = court || (
        venue ?
          _.first(_.filter(courts, ({ venue: { id: venueId } }) => venueId === venue.id)) || _.first(courts) :
          _.first(courts)
      );

    const day = date ? date.id : tourney.start;

    const scheduled = moment.tz(`${day}T12:00:00`, moment.ISO_8601, tourney.timezone).valueOf();

    this.handleEditGame({
      eventType: 'Game',
      scheduled,
      name: gameNameSearch || '',
      // if there is a court filter, use the court, otherwise if there is a venue filter,
      // use the first court in the selected venue
      court: defaultCourt,
      // if there is a game group filter, use the game group, otherwise if there is a division,
      // pick a game group in the division
      // othewise just pick the first game group
      gameGroup: defaultGameGroup
    });
  };

  handleDeleteGame = () => {
    const { dao, onSuccess, onError } = this.context;
    const { editing, promise } = this.state;

    if (!!promise) {
      return;
    }

    this.setState({
      editing: null,
      promise: dao.games.destroyId(editing.id)
        .then(
          () => {
            onSuccess(`Successfully deleted game "${editing.name}" in group "${editing.gameGroup.name}"`);
          }
        )
        .catch(onError)
        .finally(
          this.fetchData
        )
    });
  };

  cancelSwap = () => this.setState({ swapping: false });
  swapTeams = () => this.setState({ swapping: true });

  handleCompleteSwap = () => {
    this._filterDataProvider = new TourneyFilterDataProvider(this.context.dao);
    this.fetchData();
  };

  startEditAll = () => this.setState({ editAll: {} });
  cancelEditAll = () => this.setState({ editAll: null });
  changeEditAll = editAll => this.setState({ editAll });
  doEditAll = () => {
    const { dao, onError, onSuccess } = this.context;
    const { tourney } = this.props;
    const { editAll, promise, params: { filter } } = this.state;

    if (!!promise) {
      return;
    }

    this.setState({
      editAll: null,
      promise: dao.games.getAll({
        tourneyId: tourney.id,
        ...toQueryParams(filter)
      })
        .then(
          games => _.map(games, game => ({ ...game, ...editAll }))
        )
        .then(
          edited => queuePromises(_.map(edited, game => () => dao.games.save(game)))
        )
        .then(
          saved => onSuccess(`Successfully edited ${saved.length} games!`)
        )
        .catch(onError)
        .finally(
          this.fetchData
        )
    });
  };

  buttonComponent = ({ game }) => (
    <AdminFeature>
      <div className="display-flex">
        <LinkButton onClick={() => this.handleEditGame(game)}>
          <Icon name="wrench"/>
        </LinkButton>
        <LinkButton onClick={() => this.handleEditScoring(game)} className="mg-left-sm">
          <Icon name="pencil-square-o"/>
        </LinkButton>
      </div>
    </AdminFeature>
  );

  render() {
    const { location: { query }, tourney } = this.props;
    const {
      promise,
      games,
      totalCount,
      filterData,
      editing,
      scoring,
      editAll,
      swapping
    } = this.state;
    const filter = filterData ? fromQueryParams(query, filterData) : EMPTY_OBJECT;

    if (games === null) {
      if (promise === null) {
        return <EmptyState>Failed to fetch games!</EmptyState>;
      } else {
        return EmptyState.LOADING;
      }
    }

    return (
      <PageTitle title={`Games | ${tourney.name}`}>
        <LoadingWrapper loading={!!promise}>
          <SuperAdminFeature>
            <SwapTeamsModal
              open={swapping} tourneyId={tourney.id} onClose={this.cancelSwap}
              onSwapCompleted={this.handleCompleteSwap}/>
          </SuperAdminFeature>

          <AdminFeature>
            <div>
              <AdminGameModal
                game={editing} open={!!editing}
                onClose={this.cancelEdit}
                onSave={this.handleSaveEdit}
                onChange={this.handleEditGame}
                onDelete={editing && editing.id && this.handleDeleteGame}
                filterData={filterData}/>
              <EditAllModal
                editAll={editAll} open={!!editAll}
                filterData={filterData}
                onClose={this.cancelEditAll} onChange={this.changeEditAll} onSave={this.doEditAll}/>
            </div>
          </AdminFeature>

          <AdminFeature>
            <EnterScoreModal
              game={scoring} open={!!scoring}
              onClose={this.cancelEditScoring}
              onChange={this.handleEditScoring}
              onSave={this.handleEnterScore}
            />
          </AdminFeature>
          <div className="max-width">
            <div className="pd-sm">
              <AdminGamesFilter value={filter} onChange={this.changeFilter} filterData={filterData}/>
            </div>

            <div className="text-center">
              <AdminFeature>
                  <span>
                    <InverseButton className="mg-sm" onClick={this.addGame} size="sm">
                      <Icon name="plus"/>
                      <span className="mg-left-md">
                        Add Game
                      </span>
                    </InverseButton>
                    <InverseButton className="mg-sm" onClick={this.startEditAll} size="sm">
                      <Icon name="pencil"/>
                      <span className="mg-left-md">
                        Edit All
                      </span>
                    </InverseButton>
                  </span>
              </AdminFeature>
              <SuperAdminFeature>
                <InverseButton onClick={this.swapTeams} size="sm" className="mg-sm">
                  <Icon name="random"/>
                  <span className="mg-left-md">Swap Teams</span>
                </InverseButton>
              </SuperAdminFeature>
            </div>
          </div>


          <hr className="mg-bottom-xs mg-top-xs"/>

          {
            games.length === 0 ? NO_GAMES :
              <AdminGameTable
                games={games}
                querySort={filter.sort || DEFAULT_SORT}
                onSort={this.handleSort}
                buttonComponent={this.buttonComponent}
                onClickTeam={(team) => this.changeFilter({ team })}
              />
          }

          <div className="max-width">
            <QueryPagination
              { ...QueryPagination.getPageParams(query)}
              totalCount={totalCount}
              onChange={this.changeParams}
            />
          </div>
        </LoadingWrapper>
      </PageTitle>
    );
  }
}


// WEBPACK FOOTER //
// ./src/js/GameDay/components/pages/AdminGamesListPage.jsx